import { useEffect, useRef, useState, type ReactNode } from "react";
import { AUTH, BRAND, RESET_PASSWORD } from "../design/copy.js";
import { BloomBackground } from "../ui/background.js";
import { Pill, Rule } from "../ui/primitives.js";
import { isSpentLink, isTooManyTries } from "../data/auth-errors.js";
import { ProblemJsonError } from "@pushback-and-chaos/client";

/** The one request the page makes, named here for the reason check-in.tsx gives. */
export interface ResetPasswordClient {
  readonly resetPassword: (token: string, newPassword: string) => Promise<unknown>;
}

/** better-auth's minPasswordLength (apps/api better-auth.ts) and its default maxPasswordLength. */
const MIN_LENGTH = 12;
const MAX_LENGTH = 128;

type Phase = "ready" | "sending" | "done" | "spent";
type Said = "tooShort" | "tooLong" | "notThrough" | "tooMany";

const SAID: Readonly<Record<Said, string>> = {
  tooShort: AUTH.passwordTooShort,
  tooLong: AUTH.passwordTooLong,
  notThrough: RESET_PASSWORD.notThrough,
  tooMany: AUTH.tooManyTries,
};

function refusal(error: unknown): Said {
  if (isTooManyTries(error)) return "tooMany";
  if (error instanceof ProblemJsonError && error.code === "PASSWORD_TOO_SHORT") return "tooShort";
  if (error instanceof ProblemJsonError && error.code === "PASSWORD_TOO_LONG") return "tooLong";
  return "notThrough";
}

/**
 * THE RESET PAGE (`#/reset?token=`, 2026-10-22): where the password reset
 * email's link lands. Nothing happens on arrival: the token is sent only with
 * a new password, typed twice, at least 12 characters (POST
 * /auth/reset-password, the token in the body). The token works once.
 *
 * Saving signs the account out everywhere: better-auth deletes its sessions
 * (revokeSessionsOnPasswordReset) and the api expires the guest cookies the
 * account has claimed. So `onChanged` lets the app forget who this browser
 * was, and the page ends on "Your password is changed" and "sign in.",
 * whatever the app could learn. A spent link (expired, used, not one of
 * ours) is said once, with "ask for a new link." and no form; the server's
 * length refusals and a request it did not answer keep the form. One live
 * region, present from the first render, around everything that changes;
 * focus moves to the sentence that replaced the form. Claude's placeholder
 * words, in RESET_PASSWORD; the privacy card's layout.
 *
 * The link stays in the address until the server has answered in a way that
 * settles it (the pre-release review, round two, 2026-10-22): saved, or
 * spent. Then `onLinkSettled` lets the app take the token out of the history
 * entry. A length refusal, a request not answered, or a limit keeps it: the
 * link may still work, and "Open in Safari" from a mail app carries it.
 */
export function ResetPasswordPage({
  client,
  token,
  onChanged,
  onSignIn,
  onAskAgain,
  onLinkSettled,
}: {
  client: ResetPasswordClient;
  token: string | null;
  /** After the change: the app asks who this browser is now, and forgets what it cached for the account. */
  onChanged: () => Promise<void>;
  onSignIn: () => void;
  /** "ask for a new link.": the sign-in card's password reset form. */
  onAskAgain: () => void;
  /** The server's answer has settled this token (round two, 2026-10-22): the app takes it out of the address. */
  onLinkSettled: (token: string) => void;
}): ReactNode {
  const [phase, setPhase] = useState<Phase>(token === null ? "spent" : "ready");
  const [first, setFirst] = useState("");
  const [second, setSecond] = useState("");
  const [said, setSaid] = useState<Said | null>(null);
  const sent = useRef(false);
  const sentenceRef = useRef<HTMLParagraphElement>(null);
  const pressed = useRef(false);
  useEffect(() => {
    // Focus follows a press, never a cold arrival.
    if (pressed.current && (phase === "done" || phase === "spent")) sentenceRef.current?.focus();
  }, [phase]);

  const tooShort = first.length > 0 && first.length < MIN_LENGTH;
  const tooLong = first.length > MAX_LENGTH;
  const mismatch = second.length > 0 && second !== first;
  const ready = first.length >= MIN_LENGTH && !tooLong && first === second;

  const submit = async (): Promise<void> => {
    if (token === null || !ready || sent.current) return;
    sent.current = true;
    pressed.current = true;
    setSaid(null);
    setPhase("sending");
    try {
      await client.resetPassword(token, first);
    } catch (error) {
      if (isSpentLink(error)) {
        onLinkSettled(token);
        setPhase("spent");
        return;
      }
      // Refused for its length, not answered, or limited: the link may still work, and stays.
      sent.current = false;
      setSaid(refusal(error));
      setPhase("ready");
      return;
    }
    // Saved: the token has done its work.
    onLinkSettled(token);
    try {
      await onChanged();
    } catch {
      // The password is changed whatever the app could learn afterwards.
    }
    setFirst("");
    setSecond("");
    setPhase("done");
  };

  const edit = (set: (value: string) => void) => (value: string) => {
    setSaid(null);
    set(value);
  };

  const field = "mt-2 w-full border border-ink bg-paper px-4 py-2 font-sans text-body text-ink outline-none";

  return (
    <div className="relative flex min-h-svh items-start justify-center sm:py-16">
      <BloomBackground />
      <div data-testid="reset-card" className="relative z-10 w-full bg-paper px-6 py-10 sm:w-[44%] sm:min-w-[34rem] sm:max-w-[46rem] sm:px-16 sm:py-12">
        <div className="font-sans text-nav">{BRAND.short}</div>
        <Rule className="mt-2" />
        <h1 className="mt-6 mb-0 font-display text-display font-bold">{RESET_PASSWORD.heading}</h1>
        <div aria-live="polite">
          {phase === "done" ? (
            <>
              <p ref={sentenceRef} tabIndex={-1} className="mt-8 mb-0 font-sans text-body outline-none">
                {RESET_PASSWORD.done}
              </p>
              <div className="mt-8">
                <Pill onClick={onSignIn}>{RESET_PASSWORD.signIn}</Pill>
              </div>
            </>
          ) : phase === "spent" ? (
            <>
              <p ref={sentenceRef} tabIndex={-1} className="mt-8 mb-0 font-sans text-body outline-none">
                {token === null ? RESET_PASSWORD.badLink : RESET_PASSWORD.expired}
              </p>
              <div className="mt-8">
                <Pill onClick={onAskAgain}>{RESET_PASSWORD.askAgain}</Pill>
              </div>
            </>
          ) : (
            <form
              className="mt-8 flex flex-col gap-6"
              onSubmit={(e) => {
                e.preventDefault();
                void submit();
              }}
            >
              <p className="m-0 font-sans text-body">{RESET_PASSWORD.sentence}</p>
              <label className="block font-sans text-body">
                {RESET_PASSWORD.newLabel}
                <input type="password" autoComplete="new-password" value={first} onChange={(e) => edit(setFirst)(e.target.value)} className={field} />
              </label>
              <label className="block font-sans text-body">
                {RESET_PASSWORD.againLabel}
                <input type="password" autoComplete="new-password" value={second} onChange={(e) => edit(setSecond)(e.target.value)} className={field} />
              </label>
              {tooShort ? <p className="m-0 font-sans text-small text-ink-soft">{AUTH.passwordTooShort}</p> : null}
              {tooLong ? <p className="m-0 font-sans text-small text-ink-soft">{AUTH.passwordTooLong}</p> : null}
              {mismatch && !tooShort && !tooLong ? <p className="m-0 font-sans text-small text-ink-soft">{RESET_PASSWORD.mismatch}</p> : null}
              <div>
                <Pill type="submit" disabled={!ready || phase === "sending"}>
                  {RESET_PASSWORD.save}
                </Pill>
              </div>
              {said !== null ? <p className="m-0 font-sans text-small">{SAID[said]}</p> : null}
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
